import React, { useMemo, useState } from 'react';
import { CallWithAuth } from '../../actions/apiAction';
import { CREATOR_BANK_ACCOUNT } from '../../utils/apiUrl';

const ABA_WEIGHTS = [3, 7, 1, 3, 7, 1, 3, 7, 1];

function digitsOnly(value) {
  return String(value || '').replace(/\D/g, '');
}

function isValidAba(routing) {
  const digits = digitsOnly(routing);
  if (digits.length !== 9 || digits === '000000000') return false;
  const sum = digits.split('').reduce((acc, d, i) => acc + Number(d) * ABA_WEIGHTS[i], 0);
  return sum % 10 === 0;
}

function formatRouting(value) {
  const digits = digitsOnly(value).slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
}

const EMPTY = {
  account_holder_name: '',
  bank_name: '',
  routing_number: '',
  account_number: '',
  account_number_confirm: '',
  account_type: 'checking',
  authorized: false,
};

export default function BankAccountForm({ onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [showAccount, setShowAccount] = useState(false);
  const [step, setStep] = useState('form');
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');
  const [touched, setTouched] = useState({});

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setServerError('');
  };

  const routingDigits = digitsOnly(form.routing_number);
  const accountDigits = digitsOnly(form.account_number);
  const confirmDigits = digitsOnly(form.account_number_confirm);

  const errors = useMemo(() => {
    const next = {};
    if (!form.account_holder_name.trim() || form.account_holder_name.trim().length < 2) {
      next.account_holder_name = 'Enter the name on the account';
    }
    if (!form.bank_name.trim() || form.bank_name.trim().length < 2) {
      next.bank_name = 'Enter your bank name';
    }
    if (routingDigits.length !== 9) {
      next.routing_number = 'Routing number must be 9 digits';
    } else if (!isValidAba(routingDigits)) {
      next.routing_number = 'Invalid routing number';
    }
    if (accountDigits.length < 4 || accountDigits.length > 17) {
      next.account_number = 'Account number must be 4–17 digits';
    }
    if (confirmDigits && confirmDigits !== accountDigits) {
      next.account_number_confirm = 'Account numbers do not match';
    } else if (!confirmDigits) {
      next.account_number_confirm = 'Re-enter your account number';
    }
    if (!form.authorized) {
      next.authorized = 'You must authorize payouts to this account';
    }
    return next;
  }, [form, routingDigits, accountDigits, confirmDigits]);

  const showError = (name) => (touched[name] ? errors[name] : '');

  const goReview = (event) => {
    event.preventDefault();
    setTouched({
      account_holder_name: true,
      bank_name: true,
      routing_number: true,
      account_number: true,
      account_number_confirm: true,
      authorized: true,
    });
    if (Object.keys(errors).length) return;
    setStep('review');
  };

  const submit = async () => {
    setSubmitting(true);
    setServerError('');
    try {
      const res = await CallWithAuth('PUT', CREATOR_BANK_ACCOUNT, {
        account_holder_name: form.account_holder_name.trim(),
        bank_name: form.bank_name.trim(),
        routing_number: routingDigits,
        account_number: accountDigits,
        account_number_confirm: confirmDigits,
        account_type: form.account_type,
      });
      if (res?.status === 200 && res?.data?.on_file) {
        if (typeof onSaved === 'function') onSaved(res.data);
        return;
      }
      const detail = res?.data?.detail;
      setServerError(
        typeof detail === 'string' ? detail : 'Could not save bank details. Try again.'
      );
      setStep('form');
    } catch (err) {
      console.error('Failed to save bank account', err);
      setServerError('Could not save bank details. Try again.');
      setStep('form');
    } finally {
      setSubmitting(false);
    }
  };

  if (step === 'review') {
    return (
      <div className="bank-form">
        <p className="bank-form__secure">
          <span className="bank-form__lock" aria-hidden="true" />
          Encrypted · US accounts only
        </p>
        <div className="bank-review">
          <div className="bank-review__row">
            <span>Account holder</span>
            <strong>{form.account_holder_name.trim()}</strong>
          </div>
          <div className="bank-review__row">
            <span>Bank</span>
            <strong>{form.bank_name.trim()}</strong>
          </div>
          <div className="bank-review__row">
            <span>Routing number</span>
            <strong>{formatRouting(routingDigits)}</strong>
          </div>
          <div className="bank-review__row">
            <span>Account number</span>
            <strong>•••• {accountDigits.slice(-4)}</strong>
          </div>
          <div className="bank-review__row">
            <span>Account type</span>
            <strong>{form.account_type === 'savings' ? 'Savings' : 'Checking'}</strong>
          </div>
        </div>
        {serverError && <p className="bank-form__server-error">{serverError}</p>}
        <div className="bank-form__actions">
          <button
            type="button"
            className="bank-form__secondary"
            onClick={() => setStep('form')}
            disabled={submitting}
          >
            Back
          </button>
          <button
            type="button"
            className="bank-form__submit"
            onClick={submit}
            disabled={submitting}
          >
            {submitting ? 'Saving…' : 'Confirm and save'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="bank-form" onSubmit={goReview} noValidate>
      <p className="bank-form__secure">
        <span className="bank-form__lock" aria-hidden="true" />
        Encrypted · Used only for payouts · US accounts
      </p>

      <label className="bank-form__field">
        <span>Account holder name</span>
        <input
          name="account_holder_name"
          autoComplete="name"
          value={form.account_holder_name}
          onChange={(e) => setField('account_holder_name', e.target.value)}
          onBlur={() => setTouched((t) => ({ ...t, account_holder_name: true }))}
          placeholder="Name as it appears on the account"
        />
        {showError('account_holder_name') && (
          <em>{showError('account_holder_name')}</em>
        )}
      </label>

      <label className="bank-form__field">
        <span>Bank name</span>
        <input
          name="bank_name"
          value={form.bank_name}
          onChange={(e) => setField('bank_name', e.target.value)}
          onBlur={() => setTouched((t) => ({ ...t, bank_name: true }))}
          placeholder="Chase, Bank of America, …"
        />
        {showError('bank_name') && <em>{showError('bank_name')}</em>}
      </label>

      <label className="bank-form__field">
        <span>Routing number</span>
        <input
          name="routing_number"
          inputMode="numeric"
          autoComplete="off"
          value={formatRouting(form.routing_number)}
          onChange={(e) => setField('routing_number', digitsOnly(e.target.value).slice(0, 9))}
          onBlur={() => setTouched((t) => ({ ...t, routing_number: true }))}
          placeholder="XXX XXX XXX"
        />
        {showError('routing_number') && <em>{showError('routing_number')}</em>}
      </label>

      <label className="bank-form__field">
        <span>Account number</span>
        <div className="bank-form__account-row">
          <input
            name="account_number"
            inputMode="numeric"
            autoComplete="off"
            type={showAccount ? 'text' : 'password'}
            value={form.account_number}
            onChange={(e) => setField('account_number', digitsOnly(e.target.value).slice(0, 17))}
            onBlur={() => setTouched((t) => ({ ...t, account_number: true }))}
            placeholder="Enter account number"
          />
          <button
            type="button"
            className="bank-form__toggle"
            onClick={() => setShowAccount((v) => !v)}
          >
            {showAccount ? 'Hide' : 'Show'}
          </button>
        </div>
        {showError('account_number') && <em>{showError('account_number')}</em>}
      </label>

      <label className="bank-form__field">
        <span>Confirm account number</span>
        <input
          name="account_number_confirm"
          inputMode="numeric"
          autoComplete="off"
          type={showAccount ? 'text' : 'password'}
          value={form.account_number_confirm}
          onChange={(e) =>
            setField('account_number_confirm', digitsOnly(e.target.value).slice(0, 17))
          }
          onBlur={() => setTouched((t) => ({ ...t, account_number_confirm: true }))}
          placeholder="Re-enter account number"
        />
        {showError('account_number_confirm') && (
          <em>{showError('account_number_confirm')}</em>
        )}
      </label>

      <fieldset className="bank-form__type">
        <legend>Account type</legend>
        <label>
          <input
            type="radio"
            name="account_type"
            value="checking"
            checked={form.account_type === 'checking'}
            onChange={() => setField('account_type', 'checking')}
          />
          Checking
        </label>
        <label>
          <input
            type="radio"
            name="account_type"
            value="savings"
            checked={form.account_type === 'savings'}
            onChange={() => setField('account_type', 'savings')}
          />
          Savings
        </label>
      </fieldset>

      <label className="bank-form__auth">
        <input
          type="checkbox"
          checked={form.authorized}
          onChange={(e) => {
            setField('authorized', e.target.checked);
            setTouched((t) => ({ ...t, authorized: true }));
          }}
        />
        <span>
          I authorize Syntimacy to credit this bank account for payouts of my
          earnings, and I certify that I am an authorized signer on the account.
        </span>
      </label>
      {showError('authorized') && <em className="bank-form__auth-error">{showError('authorized')}</em>}

      {serverError && <p className="bank-form__server-error">{serverError}</p>}

      <button type="submit" className="bank-form__submit">
        Continue
      </button>
    </form>
  );
}
